import { useEffect, useState } from 'react';
import { shallowEqual, useSelector } from 'umi';
import http from '@/utils/http';
import { setCustomFilterList } from '@/pages/dashboard/components/NewFilter/customFilterList';
import { formatSortFiltersData } from '@/pages/dashboard/utils/_config/formatSortFiltersData';
import { IDashboardSettingState } from '@/pages/dashboard/types/dashboard';

/**
 * 事件探索筛选器专用 Hook
 * 
 * 与 useOutSelector 的区别：
 * - 只负责获取数据并返回给当前页面使用
 * - 不更新全局 selector，避免污染其他页面的筛选器数据
 * - 专门用于事件探索页面，确保页面间数据隔离
 * 
 * @param selectorApi - 事件探索筛选器 API 地址
 * @param extraSelectorParams - 额外的请求参数
 * @returns 返回筛选器数据和加载状态
 * 
 * @example
 * ```tsx
 * const EventExplorationFilter = (props) => {
 *   const { selectorData, selectorLoading } = useEventExplorationSelector({
 *     selectorApi: '/api/v1/dashboard/pc/event/exploration/select',
 *     extraSelectorParams: {}
 *   });
 *   
 *   return (
 *     <Filter 
 *       outSelector={selectorData}
 *       loading={selectorLoading}
 *       {...otherProps}
 *     />
 *   );
 * };
 * ```
 */

interface IUseEventExplorationSelectorProps {
  selectorApi?: string;
  extraSelectorParams?: Record<string, unknown>;
}

interface ICustomFilter {
  id: string;
  name: string;
  // 其他已知属性
}

interface ISelectorResponse {
  custom_filters?: Record<string, ICustomFilter>;
  game_code?: string;
  event_list?: Array<any>;
  region_list?: Array<any>;
  system_list?: Array<any>;
  app_version_list?: Array<any>;
  media_channel_list?: Array<any>;
  campaign_list?: Array<any>;
  [key: string]: any;
}

const useEventExplorationSelector = (props: IUseEventExplorationSelectorProps) => {
  const { selectorApi, extraSelectorParams } = props || {};
  const [selectorData, setSelectorData] = useState<ISelectorResponse>({});
  const [selectorLoading, setSelectorLoading] = useState<boolean>(true);

  const { gameCode } = useSelector((state: IDashboardSettingState) => {
    return {
      gameCode: state?.dashboard?.currentGame?.game_code,
    };
  }, shallowEqual);

  useEffect(() => {
    if (!selectorApi) {
      setSelectorLoading(false);
      return;
    }

    setSelectorLoading(true);
    http({
      url: selectorApi,
      method: 'post',
      data: {
        game_code: gameCode,
        ...(extraSelectorParams || {}),
      },
    })
      .then((res: any) => {
        const data = {
          gameCode,
          ...(res || {}),
          ...(res?.custom_filters || {}),
        };
        
        // 设置自定义筛选器列表（事件探索页面的自定义筛选器）
        const customFiltersKey = Object.keys(res?.custom_filters || {});
        setCustomFilterList(customFiltersKey);

        // 格式化并排序筛选器数据
        const formattedData = formatSortFiltersData(data || {});
        
        // 只返回给当前页面使用，不更新全局 selector
        // 这样可以避免事件探索页面的数据污染其他页面的筛选器
        setSelectorData(formattedData);
      })
      .catch((error: Error) => {
        console.error('获取事件探索筛选器数据失败:', error);
        setSelectorData({});
      })
      .finally(() => {
        setSelectorLoading(false);
      });
  }, [selectorApi, gameCode, extraSelectorParams]);

  return {
    selectorData,
    selectorLoading,
  };
};

export default useEventExplorationSelector;
export { useEventExplorationSelector };
